import GroupRowTemplate from "./GroupRowTemplate.js";
import UndefinedAccountGroup from "../../../chartOfAccounts/UndefinedAccountGroup.js";

/**
 * Abstract template class for rows linked to a COA accoung group.
 *
 * @abstract
 * @param {string} spec.accountGroupId - Id of the COA group
 * @param {string[]} spec.excludedGroupsIds - Ids of excluded COA groups
 */
export default class AbstractAccountGroupRowTemplate extends GroupRowTemplate {
	constructor({ accountGroupId, excludedGroupsIds = [] } = {}) {
		super(...arguments);

		this._accountGroupId = accountGroupId;
		this._excludedGroupsIds = excludedGroupsIds;
	}

	getAccountGroupId() {
		return this._accountGroupId;
	}

	setAccountGroupId(id) {
		this._accountGroupId = id;
	}

	getExcludedGroupsIds() {
		return this._excludedGroupsIds;
	}

	/**
	 * Returns the root account group row template, i.e. the first non generated
	 * account group in the parent chain.
	 */
	rootAccountGroupRowTemplate() {
		return this;
	}

	getTitle() {
		return this._title?.getValue() ? this._title : this._accountGroupId;
	}

	isAccountGroupRow() {
		return true;
	}

	/**
	 * Recursively instantiate a tree of "regular" group rows and account
	 * rows by looking up the chart of accounts of the interpretation
	 * context.
	 *
	 * Empty sub-groups are pruned away from the result.
	 */
	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);
		const context = interpreter.getContext();

		const chartOfAccounts = context.getChartOfAccounts();

		const accountGroup = this._getAccountGroup(chartOfAccounts);
		const excludedAccountGroups = this._excludedGroupsIds.map((id) =>
			this._lookupAccountGroup(chartOfAccounts, id),
		);

		const rows = this._makeGeneratedRows(interpreter, accountGroup).concat(
			this._makeAdditionalRows(interpreter),
		);

		spec.title = accountGroup.getName();
		spec.accountGroup = accountGroup;
		spec.excludedAccountGroups = excludedAccountGroups;
		spec.rows = rows;

		return spec;
	}

	_makeGeneratedRows(interpreter, accountGroup) {
		return interpreter.generateAccountGroupRows({
			parent: this,
			accountGroup,
		});
	}

	_makeAdditionalRows(interpreter) {
		return interpreter.interpretItemTemplates(this.getRows());
	}

	_instanceClass() {
		return this._subclassResponsibility();
	}

	/**
	 * Look up an account group in `chartOfAccounts`.
	 */
	_lookupAccountGroup(chartOfAccounts, accountGroupId) {
		const accountGroup = chartOfAccounts.findById(accountGroupId);
		return (
			accountGroup ||
			new UndefinedAccountGroup({ id: accountGroupId, chartOfAccounts })
		);
	}

	/**
	 * Look up my account group in `chartOfAccounts`.
	 */
	_getAccountGroup(chartOfAccounts) {
		return this._lookupAccountGroup(chartOfAccounts, this._accountGroupId);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.accountGroupId = this._accountGroupId;
		properties.excludedGroupsIds = this._excludedGroupsIds;

		return properties;
	}
}
